import React from "react";
import { DataTable as Table, Avatar, Button } from "@/components/ui";
import { FileText, ExternalLink, Download, Trash2 } from "lucide-react";
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { verveContactService } from "@/services/VerveContactService";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";

export const UploadsHistoryView = ({ history, onFileClick, onRemoveFile }) => {

  if (!history || history.length === 0) {
    return (
      <div className="card rounded-none p-12 flex flex-col items-center justify-center text-center bg-slate-50/50 border-dashed border-2">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <FileText size={32} className="text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-700">No Upload History</h3>
        <p className="text-sm text-slate-500 max-w-xs mt-1">
          You haven't uploaded any customer files yet. Bulk imports will appear here.
        </p>
      </div>
    );
  }

  const handleFileDownload = async (item, e) => {
    e.stopPropagation();
    try {
      toast.loading("Fetching all records for download...", { id: "download-toast" });
      const resp = await verveContactService.getContacts({ importId: item._id });
      const related = resp.contacts || [];

      const data = related.map(c => ({
        'First Name': c.firstName || "",
        'Last Name': c.lastName || "",
        'Phone': c.phoneNumber || "",
        'Email': c.email || 'N/A',
        'Address Line 1': c.addressLine1 || "",
        'Address Line 2': c.addressLine2 || "",
        'City': c.city || "",
        'District': c.district || "",
        'State': c.state || "",
        'PIN CODE': c.pincode || "",
        'Office Location': c.officeLocation || "",
        'Reference Type': c.referenceType || "",
        'Reference Details': c.referenceDetails || "",
        'verve Requirement': c.verveRequirement || "",
        'Capacity': c.capacity || 0,
        'Budget': c.budget || 0,
        'Notes': c.notes || "",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts");
      XLSX.writeFile(wb, item.filename || `import_${item._id}.xlsx`);
      toast.success("Download started", { id: "download-toast" });
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file", { id: "download-toast" });
    }
  };

  return (
    <div className="card rounded-none overflow-hidden">
      <Table
        headers={["File Details", "Records", "Imported By", "Date & Time", "Actions"]}
        backgroundImage={TABLE_BACKGROUNDS.uploadsHistory}
      >
        {history.map((item) => (
          <tr key={item._id} className="hover:bg-slate-50/50 transition-colors group">
            <td className="td">
              <div
                className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                onClick={() => onFileClick(item)}
                title="Click to view contacts"
              >
                <div className="w-9 h-9 bg-blue-50 text-blue-600 rounded-none flex items-center justify-center shrink-0 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  <FileText size={18} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-brand-dark truncate group-hover:text-brand-blue flex items-center gap-1.5">
                    {item.filename}
                    <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{item._id.slice(-8)}</div>
                </div>
              </div>
            </td>
            <td className="td">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-700">{item.successCount} / {item.rowCount}</span>
                <span className="text-[10px] text-slate-400 leading-none">Successful Rows</span>
              </div>
            </td>
            <td className="td">
              <div className="flex items-center gap-2">
                <Avatar name={item.importedBy?.name} size="w-6 h-6 text-[10px]" />
                <span className="text-xs font-medium text-slate-600">{item.importedBy?.name || "System"}</span>
              </div>
            </td>
            <td className="td">
              <div className="flex flex-col text-xs">
                <span className="font-medium text-slate-700">{new Date(item.createdAt).toLocaleDateString()}</span>
                <span className="text-slate-400">{new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </td>
            <td className="td">
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => handleFileDownload(item, e)}
                  variant="ghost"
                  size="icon-sm"
                  title="Download File"
                >
                  <Download size={14} className="text-slate-400" />
                </Button>
                <Button
                  onClick={(e) => { e.stopPropagation(); onRemoveFile && onRemoveFile(item._id); }}
                  variant="ghost-danger"
                  size="icon-sm"
                  title="Delete File"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
